import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular, EditRegular } from '@fluentui/react-icons';
import SubmissionFormContent, { TFormDataSubmission } from '../../SubmissionFormContent';
import { useUpdateSubmissionDetails } from '../../../api';
import { TUpdateSubmissionDialogState } from '../../../types/submissions.types';
import { ButtonLoading } from '@shared/components';
import { useRef } from 'react';

const useStyles = makeStyles({
  dialogContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
});

/**
 * UpdateSubmissionDialog
 * ---------------------------------
 * Dialog component for updating an existing submission.
 * The form is pre-filled with current submission data.
 * Uses a single state object for dialog control.
 */
const UpdateSubmissionDialog = ({
  state,
  setState,
}: {
  state: TUpdateSubmissionDialogState;
  setState: (state: TUpdateSubmissionDialogState) => void;
}) => {
  const classes = useStyles();

  // Pre-fill form values.
  // Note: The form expects jurisdiction as an array.
  const initialValues: TFormDataSubmission = {
    title: state.submission.title,
    jurisdiction: state.submission.jurisdiction ? [state.submission.jurisdiction] : [],
    dueDate: state.submission.dueDate || null,
    leads: state.submission.leads,
  };

  // API hook to update submission details.
  const updateSubmissionMutation = useUpdateSubmissionDetails();

  // onSubmit handler for updating.
  const onSubmit = (data: TFormDataSubmission) => {
    const updateData = {
      title: data.title,
      jurisdiction: data.jurisdiction[0],
      dueDate: data.dueDate || undefined,
      leads: data.leads,
    };

    updateSubmissionMutation.mutate(
      { submissionId: state.submission._id, updateData },
      {
        onSuccess: (submission) => {
          // Update the submission in the state and close the dialog.
          setState({ ...state, submission, isOpen: false });
        },
      }
    );
  };

  // Create a ref to the form component.
  const formRef = useRef<{ submit: () => void }>(null);

  return (
    <Dialog
      open={state.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) setState({ ...state, isOpen: false });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            <div className="flex items-center space-x-2">
              <EditRegular />
              <span>Update submission details</span>
            </div>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {/* Render the shared form with pre-filled values */}
            <SubmissionFormContent
              ref={formRef}
              initialValues={initialValues}
              jurisdictions={state.jurisdictions}
              onSubmit={onSubmit}
            />
          </DialogContent>
          <DialogActions>
            <ButtonLoading
              appearance="primary"
              loading={updateSubmissionMutation.isPending}
              onClick={() => formRef.current?.submit()}
            >
              Update
            </ButtonLoading>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UpdateSubmissionDialog;
